import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import RoutineStyle from '../../../../components/Routine&Performance/RoutineUpload.module.css';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    Select,
    MenuItem,
    styled,
    CircularProgress
} from '@mui/material';
import ProcessMapUploadForm from '../../Forms/ProcessMapUploadForm/ProcessMapUploadForm';
import { X } from 'lucide-react';
import { apiService } from '../../../../api/apiService';
import useFunctionOptions from '../../utils/useFunctionOptions';
import SuccessPopup from '../../Popup/Success&ErrorPopup/SuccessPopup';
import ErrorPopup from '../../Popup/Success&ErrorPopup/ErrorPopup';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';


interface ProcessFile {
    id: string;
    name: string;
    date: string;
    functionL1: string;
    month: string;
    year: string;
    status: string;
    size: string;
}

interface ProcessMapPopupProps {
    openUpload: boolean;
    setOpenUpload: Dispatch<SetStateAction<boolean>>;
    management: string;
    verticalTabValue?: string;
    onSuccess?: () => void;
}

const StyledMenuItem = styled(MenuItem)({
    color: 'black',
    '&:hover': {
        backgroundColor: '#f0f0f0',
    },
});

const ProcessMapPopup: React.FC<ProcessMapPopupProps> = ({ openUpload, setOpenUpload, management, verticalTabValue, onSuccess }) => {
    const { instance } = useMsal();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [files, setFiles] = useState<ProcessFile[]>([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [functionL1, setFunctionL1] = useState('');
    const [functionL2, setFunctionL2] = useState('');
    const [functionL3, setFunctionL3] = useState('');
    const [functionL4, setFunctionL4] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');
    const { options: l1Options, loading: l1Loading } = useFunctionOptions('l1', '');
    const { options: l2Options, loading: l2Loading } = useFunctionOptions('l2', functionL1);
    const { options: l3Options, loading: l3Loading } = useFunctionOptions('l3', functionL2);
    const { options: l4Options, loading: l4Loading } = useFunctionOptions('l4', functionL3);

    const currentYear = new Date().getFullYear();
    const years = Array.from(new Array(10), (val, index) => currentYear - 1 + index);

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
    };

    const handleSubmit = async () => {
        if (selectedFile) {
            const formData = new FormData();

            // Dynamically construct the payload based on filled fields
            const processMapReq: Record<string, string> = {};

            if (verticalTabValue) processMapReq.section_name = verticalTabValue;
            if (management) processMapReq.management = management;
            if (functionL1) processMapReq.function_l1 = functionL1;
            if (functionL2) processMapReq.function_l2 = functionL2;
            if (functionL3) processMapReq.function_l3 = functionL3;
            if (functionL4) processMapReq.function_l4 = functionL4;
            if (month) processMapReq.month = month;
            if (year) processMapReq.year = year;

            formData.append("performance_req", JSON.stringify(processMapReq));
            formData.append("file", selectedFile);

            setUploading(true);
            try {
                const result = await apiService("/management/mcrs", "POST", formData, undefined, instance as PublicClientApplication );
                setFiles([
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        name: selectedFile.name,
                        date: new Date().toLocaleDateString(),
                        functionL1,
                        month,
                        year,
                        status: "Submitted",
                        size: `${Math.round(selectedFile.size / 1024)} KB`,
                    },
                    ...files,
                ]);

                setOpenUpload(false);
                resetUploadState();
                setShowSuccess(true);
                if (onSuccess) {
                    onSuccess(); // Call the callback function after successful upload
                }
            } catch (error) {
                console.error("Error during submission:", error);
                setErrorMessage("Failed to upload the file. Please try again.");
                setShowError(true);
            } finally {
                setUploading(false);
            }
        }
    };

    const resetUploadState = () => {
        setSelectedFile(null);
        setFunctionL1('');
        setFunctionL2('');
        setFunctionL3('');
        setMonth('');
        setYear('');
    };


    const getFileTitle = () => {
        switch (verticalTabValue) {
            case "Routine & Performance Review":
                return "Upload the month wise PRP deck which includes E2E KPI reporting along with evidence of the invite sent to the L1 functional directors";
            case "Routine Stakeholder Connects":
                return "Upload action log from routine stakeholder connects";
            case "TOR(Terms of Reference)":
                return "TOR document  signed or approved by relevant stakeholders which includes meeting objectives, action plans, attendees, and agenda. Critical meetings are: PRP, Stakeholder connections, 1:1 and Stercos.  connections,  The L1 process PRP meeting must include E2E KPI reporting, with L1 process directors as participants";
            case "Escalation Management":
                return "Provide records or dashboards showing how critical escalations and key learnings are tracked, such as issue tracking systems, escalation logs, or learning databases. Evidence should include metrics or key issues that have been tracked and resolved.";
            case "Target Management":
                return "Provide evidence from the Northstar platform showing that all targets for the center are tracked. Evidence should include platform logs or screenshots showing target entries and tracking status.";
            default:
                return "Upload file";
        }
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showSuccess) {
            timer = setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [showSuccess]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showError) {
            timer = setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [showError]);

    return (
        <>
            <Dialog
                open={openUpload}
                onClose={() => {
                    setOpenUpload(false);
                    resetUploadState();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#000', fontSize: '16px', fontFamily: "Avant semibold",
                    fontWeight: 600,
                }}>
                    {verticalTabValue}
                    <IconButton onClick={() => setOpenUpload(false)}>
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Function L1 *</InputLabel>
                            <Select
                                sx={{ borderRadius: '8px', color: '#000' }}
                                value={functionL1}
                                label="Function L1 *"
                                onChange={(e) => setFunctionL1(e.target.value)}
                            >
                                <StyledMenuItem value="">Select Function L1</StyledMenuItem>
                                {l1Loading ? (
                                    <StyledMenuItem value="">
                                        <CircularProgress size={24} />
                                    </StyledMenuItem>
                                ) : (
                                    l1Options.map((option, index) => (
                                        <StyledMenuItem key={index} value={option}>
                                            {option}
                                        </StyledMenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        {verticalTabValue === "Routine & Performance Review" && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '48% 48%', justifyContent: 'space-between' }}>
                                <FormControl className={RoutineStyle.select_form} fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Month *</InputLabel>
                                    <Select
                                        value={month}
                                        label="Month *"
                                        onChange={(e) => setMonth(e.target.value)}
                                        sx={{ borderRadius: '8px', color: '#000' }}
                                        className='functionselection'
                                    >
                                        {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month) => (
                                            <StyledMenuItem key={month} value={month}>
                                                {month}
                                            </StyledMenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Year *</InputLabel>
                                    <Select
                                        value={year}
                                        label="Year *"
                                        onChange={(e) => setYear(e.target.value)}
                                        sx={{ borderRadius: '8px', color: '#000' }}
                                        className='functionselection'
                                    >
                                        {years.map((year) => (
                                            <StyledMenuItem key={year} value={year}>
                                                {year}
                                            </StyledMenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        {verticalTabValue === "Target Management" && (
                            <>
                                {/* Function L2 */}
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Function L2 *</InputLabel>
                                    <Select
                                        sx={{ borderRadius: '8px', color: '#000' }}
                                        value={functionL2}
                                        label="Function L2 *"
                                        onChange={(e) => setFunctionL2(e.target.value)}
                                        disabled={!functionL1}
                                    >
                                        <StyledMenuItem value="">Select Function L2</StyledMenuItem>
                                        {l2Loading ? (
                                            <StyledMenuItem value="">
                                                <CircularProgress size={24} />
                                            </StyledMenuItem>
                                        ) : (
                                            l2Options.map((option, index) => (
                                                <StyledMenuItem key={index} value={option}>
                                                    {option}
                                                </StyledMenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>

                                {/* Function L3 */}
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Function L3 *</InputLabel>
                                    <Select
                                        sx={{ borderRadius: '8px', color: '#000' }}
                                        value={functionL3}
                                        label="Function L3 *"
                                        onChange={(e) => setFunctionL3(e.target.value)}
                                        disabled={!functionL2}
                                    >
                                        <StyledMenuItem value="">Select Function L3</StyledMenuItem>
                                        {l3Loading ? (
                                            <StyledMenuItem value="">
                                                <CircularProgress size={24} />
                                            </StyledMenuItem>
                                        ) : (
                                            l3Options.map((option, index) => (
                                                <StyledMenuItem key={index} value={option}>
                                                    {option}
                                                </StyledMenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </Box>
                    <ProcessMapUploadForm
                        file={selectedFile}
                        onFileSelect={handleFileSelect}
                        onFileRemove={() => setSelectedFile(null)}
                        fileTitle={getFileTitle()}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button variant='outlined' onClick={() => setOpenUpload(false)} sx={{ color: '#000', borderColor: '#000' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!selectedFile || uploading}
                        sx={{
                            color: 'white',
                            bgcolor: 'black',
                            '&:hover': { bgcolor: 'black' },
                            '&:disabled': { bgcolor: 'grey.300' },
                        }}
                    >
                        {uploading ? 'Uploading...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Popup */}
            <SuccessPopup
                open={showSuccess}
                onClose={() => setShowSuccess(false)}
                message={`${verticalTabValue} uploaded successfully`}
            />

            {/* Error Popup */}
            <ErrorPopup
                open={showError}
                onClose={() => setShowError(false)}
                message={errorMessage}
            />
        </>
    );
};

export default ProcessMapPopup;